import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Delta, EmptyState, HalalBadge, LiveBadge } from "@/components/common";
import { MarketsTabs } from "@/components/markets/MarketsTabs";
import { TradeSheet } from "@/components/order/TradeSheet";
import { NewsFeed } from "@/components/NewsFeed";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtCcy, fmtUSD, timeAgo } from "@/lib/format";
import { Search, Star, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { toastError } from "@/lib/toastError";
import { useLiveQuotes } from "@/hooks/useLiveQuotes";

export default function TradeGlobal() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [instruments, setInstruments] = useState([]);
  const [search, setSearch] = useState("");
  const [watchlist, setWatchlist] = useState([]);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ticket, setTicket] = useState({ open: false, symbol: null, side: "buy", type: "market", qty: null });

  const load = async () => {
    const [inst, wl, pos, ord] = await Promise.all([
      api.get("/market/instruments?market=US"),
      api.get("/watchlist"),
      api.get("/positions?source=global"),
      api.get("/orders?source=global"),
    ]);
    setInstruments(inst.data); setWatchlist(wl.data); setPositions(pos.data); setOrders(ord.data);
  };
  useEffect(() => { load(); const iv = setInterval(load, 6000); return () => clearInterval(iv); }, []);

  const addWatch = async (symbol) => {
    try { await api.post("/watchlist", { symbol }); toast.success(`${symbol} added to watchlist`); load(); }
    catch (e) { toastError("Couldn't add to watchlist", e); }
  };
  const removeWatch = async (symbol) => {
    try { await api.delete(`/watchlist/${symbol}`); load(); }
    catch (e) { toastError("Couldn't remove from watchlist", e); }
  };

  const { quotes: live, connected } = useLiveQuotes(instruments.slice(0, 40).map((i) => i.symbol));
  const filtered = instruments.filter((i) => !search || i.symbol.toLowerCase().includes(search.toLowerCase()) || i.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <AppShell>
      <PageHeader title={t("tr_us_title")} subtitle={t("tr_us_sub")}
        action={<div className="flex items-center gap-2"><LiveBadge />{connected && <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid="trade-global-stream">streaming</span>}</div>} testid="trade-global-header" />

      <MarketsTabs />

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0 space-y-4 sm:space-y-5">
          <Card className="p-4 shadow-premium">
            <div className="relative mb-3">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search tickers (AAPL, VOO...)" value={search} onChange={(e) => setSearch(e.target.value)}
                className="ps-9" data-testid="global-search-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[420px] overflow-auto" data-testid="global-price-cards">
              {filtered.map((s, i) => (
                <div key={`${s.symbol}-${i}`} className="rounded-lg border p-3 hover:bg-accent transition-colors cursor-pointer group"
                  onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>
                  <div className="flex items-start justify-between">
                    <div><div className="font-medium flex items-center gap-2">{s.symbol} {s.halal && <HalalBadge />}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[160px]">{s.name}</div></div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); addWatch(s.symbol); }} data-testid={`watch-add-${s.symbol}`}>
                      <Star className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-end justify-between mt-2">
                    <span className="numeric text-lg font-semibold" data-testid={`price-${s.symbol}`}>{fmtCcy(live[s.symbol]?.price ?? s.price, s.currency)}</span>
                    <Delta value={live[s.symbol]?.change_pct ?? s.change_pct} className="text-sm" />
                  </div>
                  <Button size="sm" className="w-full mt-2" onClick={(e) => { e.stopPropagation(); setTicket({ open: true, symbol: s.symbol, side: "buy", type: "market", qty: null }); }}
                    data-testid={`trade-btn-${s.symbol}`}>{t("tr_trade")}</Button>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-4 shadow-premium">
            <Tabs defaultValue="positions">
              <TabsList><TabsTrigger value="positions" data-testid="tab-positions">{t("tr_positions")}</TabsTrigger><TabsTrigger value="orders" data-testid="tab-orders">{t("tr_order_history")}</TabsTrigger></TabsList>
              <TabsContent value="positions" className="mt-3 overflow-x-auto">
                {positions.length ? (
                  <Table data-testid="positions-table">
                    <TableHeader><TableRow><TableHead>{t("tr_symbol")}</TableHead><TableHead className="text-end">{t("cj_qty")}</TableHead><TableHead className="text-end">{t("tr_avg")}</TableHead><TableHead className="text-end">{t("cj_value")}</TableHead><TableHead className="text-end">{t("tr_pnl")}</TableHead><TableHead className="text-end">{t("tr_protect")}</TableHead></TableRow></TableHeader>
                    <TableBody>{positions.map((p) => (
                      <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/stock/${encodeURIComponent(p.symbol)}`)}>
                        <TableCell className="font-medium">{p.symbol}</TableCell>
                        <TableCell className="text-end numeric">{p.qty}</TableCell>
                        <TableCell className="text-end numeric">{fmtCcy(p.avg_cost, p.currency)}</TableCell>
                        <TableCell className="text-end numeric">{fmtUSD(p.market_value_usd)}</TableCell>
                        <TableCell className="text-end"><Delta value={p.pnl_pct} /></TableCell>
                        <TableCell className="text-end">
                          <Button variant="outline" size="sm" className="gap-1.5"
                            onClick={(e) => { e.stopPropagation(); setTicket({ open: true, symbol: p.symbol, side: "sell", type: "stop", qty: p.qty }); }}
                            data-testid={`protect-position-${p.symbol}`}>
                            <ShieldCheck className="h-3.5 w-3.5" />{t("tr_protect")}
                          </Button>
                        </TableCell>
                      </TableRow>))}</TableBody>
                  </Table>
                ) : <EmptyState title="No positions" hint="Buy a stock to open your first position" />}
              </TabsContent>
              <TabsContent value="orders" className="mt-3 overflow-x-auto">
                {orders.length ? (
                  <Table data-testid="orders-table">
                    <TableHeader><TableRow><TableHead>{t("tr_symbol")}</TableHead><TableHead>Side</TableHead><TableHead className="text-end">{t("cj_qty")}</TableHead><TableHead className="text-end">Price</TableHead><TableHead className="text-end">Time</TableHead></TableRow></TableHeader>
                    <TableBody>{orders.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell className="font-medium">{o.symbol}</TableCell>
                        <TableCell><Badge variant={o.side === "buy" ? "default" : "destructive"} className="capitalize">{o.side}</Badge></TableCell>
                        <TableCell className="text-end numeric">{o.qty}</TableCell>
                        <TableCell className="text-end numeric">{fmtCcy(o.fill_price, o.currency)}</TableCell>
                        <TableCell className="text-end text-xs text-muted-foreground">{timeAgo(o.timestamp)}</TableCell>
                      </TableRow>))}</TableBody>
                  </Table>
                ) : <EmptyState title="No orders yet" hint="Your executed trades will appear here" />}
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <div className="min-w-0 space-y-4 sm:space-y-5">
        <Card className="p-4 shadow-premium h-fit" data-testid="global-watchlist">
          <div className="flex items-center gap-2 text-sm font-medium mb-3"><Star className="h-4 w-4 text-[color:var(--tharwy-lime)]" />{t("tr_watchlist")}</div>
          {watchlist.length ? (
            <div className="space-y-2">{watchlist.map((s) => (
              <div key={`wl-${s.symbol}`} className="flex items-center justify-between rounded-lg border px-3 py-2 hover:bg-accent transition-colors cursor-pointer"
                onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>
                <div><div className="font-medium text-sm">{s.symbol}</div><div className="text-xs text-muted-foreground truncate max-w-[120px]">{s.name}</div></div>
                <div className="text-end"><div className="numeric text-sm">{fmtCcy(s.price, s.currency)}</div><Delta value={s.change_pct} className="text-xs" /></div>
              </div>
            ))}</div>
          ) : <EmptyState title="Empty watchlist" hint="Add tickers from the list" />}
        </Card>
        <NewsFeed market="US" title={t("tr_news_us")} limit={16} height="360px" />
        </div>
      </div>

      <TradeSheet open={ticket.open} onOpenChange={(v) => setTicket({ ...ticket, open: v })} symbol={ticket.symbol}
        source="global" initialSide={ticket.side} initialType={ticket.type} maxQty={ticket.qty} onDone={load} />
    </AppShell>
  );
}
